import F84Game from "../Game";
import LoaderScene from "../scenes/LoaderScene";
import ArrayUtils from "../utils/ArrayUtils";
import BaseURL from "../utils/BaseURL";
import LottieUtils from "../utils/LottieUtils";

class AssetType {
	static readonly IMAGE = 0;
	static readonly SOUND = 1;
	static readonly JSON = 2;
	static readonly ATLAS = 3;
	static readonly LOTTIE = 4;
	static readonly LOTTIE_ANIMATION = 5;
	static readonly GLTF = 6;
	static readonly SPRITESHEET = 7;
	static readonly VIDEO = 8;
	static readonly MULTI_ATLAS = 9;
	static readonly SPINE = 10;
}

class LoadAsset {
	key: string;
	path: string;
	data: { [k: string]: any };
}

export default class LoadBatch {
	key: string;
	assets: Map<number, LoadAsset[]>;

	loaded = false;

	constructor(key: string) {
		this.key = key;
		this.assets = new Map<number, LoadAsset[]>();
	}

	add(key: string, path: string, type: number, data?: { [k: string]: any }): void {
		if (!this.assets.has(type)) {
			this.assets.set(type, []);
		}
		this.assets.get(type).push({ key: key, path: path, data: data });
	}

	addImage(key: string, path: string, hasLowQuality = true): void {
		this.add(key, path, AssetType.IMAGE, { hasLowQuality: hasLowQuality });
	}

	addImages(prefix: string, map: { [k: string]: string }, suffix = ".png", hasLowQuality = true): void {
		for (const name in map) {
			this.addImage(name, prefix + map[name] + suffix, hasLowQuality);
		}
	}

	addVideo(prefix: string, map: { [k: string]: string }, suffix = ".mp4"): void {
		for (const name in map) {
			this.add(name, prefix + map[name] + suffix, AssetType.VIDEO, { hasLowQuality: false });
		}
	}

	addJson(key: string, path: string, hasLowQuality = true): void {
		this.add(key, path, AssetType.JSON, { hasLowQuality: hasLowQuality });
	}

	addSound(key: string, path: string): void {
		this.add(key, path, AssetType.SOUND);
	}

	addSounds(prefix: string, map: { [k: string]: string }, suffix = ".mp3"): void {
		for (const name in map) {
			this.addSound(name, prefix + map[name] + suffix);
		}
	}

	addAtlas(key: string, path: string, img: string, json: string, hasLowQuality = true): void {
		this.add(key, path, AssetType.ATLAS, { img: img, json: json, hasLowQuality: hasLowQuality });
	}

	// MultiAtlasing does not currently support half-res spritesheets
	addMultiAtlas(key: string, path: string, json: string, hasLowQuality = false): void {
		this.add(key, path, AssetType.MULTI_ATLAS, { json: json, hasLowQuality: hasLowQuality });
	}

	addSpriteSheet(key: string, path: string, frameWidth: number, frameHeight: number, startFrame: number, endFrame: number): void {
		this.add(key, path, AssetType.SPRITESHEET, {
			frameWidth: frameWidth,
			frameHeight: frameHeight,
			startFrame: startFrame,
			endFrame: endFrame,
		});
	}

	addLottie(key: string, path: string, width: number, height: number, spritesheet: boolean): void {
		this.add(key, path, AssetType.LOTTIE, { width: width, height: height, spritesheet: spritesheet });
	}

	addLottieAnimation(key: string, path: string, spritesheet: boolean): void {
		this.add(key, path, AssetType.LOTTIE_ANIMATION, { spritesheet: spritesheet });
	}

	addGLTF(key: string, path: string): void {
		this.add(key, path, AssetType.GLTF);
	}

	addSpine(key: string, path: string, filename: string, preMultipliedAlpha?: boolean): void {
		this.add(key, path, AssetType.SPINE, { filename: filename, preMultipliedAlpha: preMultipliedAlpha });
	}

	load(loader: LoaderScene, forceLoad?: boolean, onLoaded?: () => void): void {
		if (forceLoad) this.loaded = false;

		if (this.loaded) return;

		this.assets.forEach((value, key) => {
			value.forEach((asset) => {
				switch (key) {
					case AssetType.IMAGE:
						loader.loadImage(asset.key, asset.path, asset.data.hasLowQuality);
						break;
					case AssetType.JSON:
						loader.loadJson(asset.key, asset.path, asset.data.hasLowQuality);
						break;
					case AssetType.ATLAS:
						loader.loadAtlas(
							asset.key,
							asset.path + asset.data.img + ".png",
							asset.path + asset.data.json + ".json",
							asset.data.hasLowQuality
						);
						break;
					case AssetType.SOUND:
						loader.load.audio(asset.key, asset.path);
						break;
					case AssetType.LOTTIE:
						loader.loadLottie(
							asset.path,
							asset.key,
							asset.data.width,
							asset.data.height,
							asset.data.spritesheet,
							asset.data.backgroundOnly
						);
						break;
					case AssetType.LOTTIE_ANIMATION:
						loader.loadLottieAnimation(BaseURL.getBaseURL() + asset.path.replace(".", ""), asset.key, asset.data.spritesheet);
						break;
					case AssetType.GLTF:
						loader.load.gltf(asset.key, asset.path);
						break;
					case AssetType.SPRITESHEET:
						loader.loadSpritesheet(asset.key, BaseURL.getBaseURL() + asset.path, {
							frameWidth: asset.data["frameWidth"],
							frameHeight: asset.data["frameHeight"],
							startFrame: asset.data["startFrame"],
							endFrame: asset.data["endFrame"],
						});
						break;
					case AssetType.VIDEO:
						loader.loadVideo(asset.key, asset.path);
						break;
					case AssetType.MULTI_ATLAS:
						loader.loadMultiAtlas(asset.key, asset.path + asset.data.json + ".json", asset.path, asset.data.hasLowQuality);
						break;
					case AssetType.SPINE:
						loader.loadSpine(asset.key, asset.path, asset.data.filename, asset.data.preMultipliedAlpha);
						break;
				}
			});
		});

		const keys: string[] = [];
		this.assets.forEach((value) => {
			value.forEach((asset) => {
				keys.push(asset.key);
			});
		});

		const fileLoaded = (key: string, _type: string): void => {
			ArrayUtils.remove(keys, key);
			if (keys.length == 0) loadFinished();
		};
		loader.load.on("filecomplete", fileLoaded);

		const loadFinished = (): void => {
			loader.load.off("filecomplete", fileLoaded);
			if (onLoaded) onLoaded();
		};

		if (keys.length == 0) loadFinished();

		this.loaded = true;
	}

	unload(): void {
		if (!this.loaded) return;

		const game = F84Game.Instance;
		let imageKeys = null;
		this.assets.forEach((value, key) => {
			value.forEach((asset) => {
				switch (key) {
					case AssetType.IMAGE:
						if (game.textures.exists(asset.key)) game.textures.remove(asset.key);
						break;
					case AssetType.JSON:
						if (game.cache.json.exists(asset.key)) game.cache.json.remove(asset.key);
						break;
					case AssetType.ATLAS:
						if (game.textures.exists(asset.key)) game.textures.remove(asset.key);
						if (game.cache.json.exists(asset.key)) game.cache.json.remove(asset.key);
						if (game.anims.exists(asset.key)) game.anims.remove(asset.key);
						break;
					case AssetType.SOUND:
						if (game.cache.audio.exists(asset.key)) game.cache.audio.remove(asset.key);
						break;
					case AssetType.LOTTIE:
						LottieUtils.unloadAnimation(asset.key);
						break;
					case AssetType.LOTTIE_ANIMATION:
						if (asset.data.spritesheet) {
							if (game.textures.exists(asset.key + "-atlas")) game.textures.remove(asset.key + "-atlas");
							if (game.cache.json.exists(asset.key + "-atlas")) game.cache.json.remove(asset.key + "-atlas");
						} else {
							imageKeys = game.textures.getTextureKeys().filter((t) => t.indexOf(asset.key + "-") != -1);
							imageKeys.forEach((key) => {
								if (game.textures.exists(key)) game.textures.remove(key);
							});
						}
						if (game.cache.json.exists(asset.key + "-animData")) game.cache.json.remove(asset.key + "-animData");
						break;
					case AssetType.GLTF:
						if (game.cache.binary.exists(asset.key)) game.cache.binary.remove(asset.key);
						break;
					case AssetType.SPRITESHEET:
						if (game.textures.exists(asset.key)) game.textures.remove(asset.key);
						if (game.anims.exists(asset.key)) game.anims.remove(asset.key);
						break;
					case AssetType.VIDEO:
						if (game.cache.video.exists(asset.key)) game.cache.video.remove(asset.key);
						break;
				}
			});
		});

		this.loaded = false;
	}
}
